import {
  accessibilityDataAtom,
  currentAuditEntryAtom,
  reportDemoModeAtom,
  userAtom,
} from '@/global-store';
import userHasAccessToEntry from '@/helpers/userHasAccessToEntry';
import LevelIcon from '@components/LevelIcon';
import { useUpdateConformanceTarget } from '@http/queries';

import NoSaveInDemoBanner from '@reportComponents/NoSaveInDemoBanner';
import { useAtom } from 'jotai';
import { type Dispatch, type SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Checkbox,
  CircularProgress,
  Combobox,
  type DropDownItem,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
  RadioGroup,
  RadioGroupItem,
  Tooltip,
  DialogDescription,
  DialogActions,
} from '@eye-able/ui';
import type { ConformanceTarget, WcagLevel } from '@eye-able/types';

const conformanceItems: DropDownItem[] = [
  { value: 'A', label: 'A' },
  { value: 'AA', label: 'AA' },
  { value: 'AAA', label: 'AAA' },
  { value: 'Best Practice', label: 'Best Practice' },
];

const ConformanceTargetSelect = () => {
  const { t } = useTranslation();
  const [user] = useAtom(userAtom);
  const [accessibilityData] = useAtom(accessibilityDataAtom);
  const [reportDemoMode] = useAtom(reportDemoModeAtom);
  const [entry] = useAtom(currentAuditEntryAtom);
  const mutation = useUpdateConformanceTarget();

  const [targetLevel, setTargetLevel] = useState<string | null>(null);
  const [bestPractiseChecked, setBestPractiseChecked] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const conformanceTarget = accessibilityData?.conformanceTarget;

  useEffect(() => {
    setSelectionsFromConformanceTarget(
      conformanceTarget,
      setTargetLevel,
      setBestPractiseChecked,
    );

    return () => {
      setTargetLevel(null);
      setBestPractiseChecked(false);
    };
  }, [conformanceTarget]);

  const handleTargetSave = async () => {
    if (!targetLevel) return;

    const newTarget =
      targetLevel + (bestPractiseChecked ? ' + Best Practice' : '');

    if (newTarget === conformanceTarget) return;

    await mutation.mutateAsync(newTarget);
  };

  const handleConfirm = async () => {
    if (isSaving || !targetLevel) return;

    setIsSaving(true);
    await handleTargetSave();
    setIsSaving(false);
    setDialogOpen(false);
  };

  const handleDialogClose = () => {
    setSelectionsFromConformanceTarget(
      conformanceTarget,
      setTargetLevel,
      setBestPractiseChecked,
    );
    setDialogOpen(false);
  };

  const handleBestPractiseChange = (checked: boolean | 'indeterminate') => {
    setBestPractiseChecked(checked === true);
  };

  if (!accessibilityData) return null;

  const auditScore = accessibilityData.calcOverallAuditScore(
    accessibilityData.auditScores,
    false,
    targetLevel + (bestPractiseChecked ? ' + Best Practice' : ''),
  );

  return (
    <div className='eye:max-sm:w-full!'>
      <Dialog
        open={dialogOpen}
        onOpenChange={(open) =>
          open ? setDialogOpen(true) : handleDialogClose()
        }
      >
        <DialogTrigger asChild>
          <div
            aria-label={`${t('target')}: ${conformanceTarget}`}
            className='eye:w-full! eye:cursor-pointer'
            role='button'
            tabIndex={0}
          >
            <Tooltip content={t('conformanceDialogButtonTooltip')} asChild>
              <span className='eye:block'>
                <Combobox
                  className='eye:w-full'
                  multiSelect
                  aria-label={t('target')}
                  labelProps={{
                    float: 'top',
                    label: <span className='eye:mr-5xs'>{t('target')}:</span>,
                    className: 'eye:min-w-[150px]',
                    labelFloatClassName: 'eye:bg-grey-5',
                  }}
                  value={getComboboxValue(conformanceTarget)}
                  items={conformanceItems}
                  open={false}
                  itemRenderer={(item: DropDownItem) => (
                    <LevelIcon level={item.label as WcagLevel} />
                  )}
                />
              </span>
            </Tooltip>
          </div>
        </DialogTrigger>
        <DialogContent className='eye:w-full eye:max-w-[720px]'>
          <DialogTitle>{t('conformanceTargetHeading')}</DialogTitle>
          <DialogDescription>
            <div className='eye:grid eye:grid-cols-2 eye:gap-4xs'>
              <div>
                <div className='eye:pb-4xs'>
                  {t('conformanceTargetSubhead')}
                </div>
                <RadioGroup onValueChange={setTargetLevel} value={targetLevel}>
                  <RadioGroupItem
                    value='A'
                    label={<LevelIcon level='A' showName />}
                  />
                  <RadioGroupItem
                    value='AA'
                    label={
                      <div className='eye:flex eye:items-center eye:gap-4xs'>
                        <LevelIcon level='A' />
                        <LevelIcon level='AA' showName />
                      </div>
                    }
                  />
                  <RadioGroupItem
                    value='AAA'
                    label={
                      <div className='eye:flex eye:items-center eye:gap-4xs'>
                        <LevelIcon level='A' />
                        <LevelIcon level='AA' />
                        <LevelIcon level='AAA' showName />
                      </div>
                    }
                  />
                </RadioGroup>
                <div className='eye:pt-xs eye:pb-4xs'>
                  {t('conformanceTargetText')}
                </div>
                <Checkbox
                  checked={bestPractiseChecked}
                  onCheckedChange={handleBestPractiseChange}
                  label={<LevelIcon level='Best Practice' showName />}
                  id='best-practice-checkbox'
                />
              </div>
              <div className='eye:w-4/5 eye:ml-auto'>
                <CircularProgress
                  value={Number(auditScore) || 0}
                  size='big'
                  ariaLabel={t('accessibilityHeading2')}
                />
              </div>
              <NoSaveInDemoBanner className='eye:col-span-2 eye:mt-xs' />
            </div>
          </DialogDescription>
          <DialogActions>
            <Button
              variant='outline'
              onClick={handleDialogClose}
              disabled={isSaving}
            >
              {t('cancel')}
            </Button>
            <Button
              variant='yellow'
              onClick={() => void handleConfirm()}
              disabled={
                isSaving || reportDemoMode || !userHasAccessToEntry(user, entry)
              }
            >
              {t('save')}
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>
    </div>
  );
};

function getComboboxValue(conformanceTarget?: ConformanceTarget) {
  if (!conformanceTarget) return [];

  const levels = [];
  // order matters to make WCAG mutually exclusive
  if (conformanceTarget.includes('AAA')) levels.push('AAA');
  else if (conformanceTarget.includes('AA')) levels.push('AA');
  else if (conformanceTarget.includes('A')) levels.push('A');

  if (conformanceTarget.includes('Best Practice')) levels.push('Best Practice');

  return levels;
}

function setSelectionsFromConformanceTarget(
  conformanceTarget: ConformanceTarget | undefined,
  setSelectedTargetLevel: Dispatch<SetStateAction<string | null>>,
  setBestPractiseChecked: Dispatch<SetStateAction<boolean>>,
) {
  if (!conformanceTarget) return;
  const split = conformanceTarget.split(' + ');

  setSelectedTargetLevel(split[0]);
  setBestPractiseChecked(!!split[1]);
}

export default ConformanceTargetSelect;
